import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { errorMessage, post } from '../api'
import { useAuth } from '../auth'
import { PeriodsPreview, PlaceFinder, type PlaceDetails, type SuggestedPeriod } from '../components/PlaceFinder'
import { Alert, Button, Field, Input, Select } from '../components/ui'
import { WEEKDAYS } from '../format'
import { rememberBusiness } from './Home'

// Creating a restaurant in three short steps. Everything here can be changed later in Settings.

interface AreaDraft {
  name: string
  capacityCovers: number
}
interface PeriodDraft {
  name: string
  daysOfWeek: number[]
  starts: string
  ends: string
}

const BOOKING_PAGE_URL = (import.meta.env.VITE_BOOKING_PAGE_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5173'

const TIMEZONES = ['Europe/London', 'Europe/Istanbul', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam', 'Europe/Dublin', 'Europe/Lisbon', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Dubai', 'Australia/Sydney']

/** The zone's current offset from UTC in minutes (east positive), the way Google reports a place's. */
function zoneOffsetMinutes(tz: string): number {
  const now = new Date()
  const local = new Date(now.toLocaleString('en-US', { timeZone: tz }))
  const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
  return Math.round((local.getTime() - utc.getTime()) / 60_000)
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

export function OnboardingPage() {
  const { me, refresh } = useAuth()
  const navigate = useNavigate()
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [timezone, setTimezone] = useState(TIMEZONES.includes(browserTz) ? browserTz : 'Europe/London')
  const [currency, setCurrency] = useState(browserTz === 'Europe/Istanbul' ? 'TRY' : 'GBP')
  const [email, setEmail] = useState(me?.user.email ?? '')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState('')
  const [website, setWebsite] = useState('')
  const [placeId, setPlaceId] = useState('')
  const [googleHours, setGoogleHours] = useState<SuggestedPeriod[] | null>(null)
  const [useGoogleHours, setUseGoogleHours] = useState(true)
  const [areas, setAreas] = useState<AreaDraft[]>([{ name: 'Dining room', capacityCovers: 40 }])
  const [periods, setPeriods] = useState<PeriodDraft[]>([
    { name: 'Lunch', daysOfWeek: [1, 2, 3, 4, 5, 6, 0], starts: '12:00', ends: '15:00' },
    { name: 'Dinner', daysOfWeek: [1, 2, 3, 4, 5, 6, 0], starts: '18:00', ends: '22:30' },
  ])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setNameAndSlug = (v: string) => {
    setName(v)
    if (!slugTouched) setSlug(slugify(v))
  }

  const applyPlace = (place: PlaceDetails, suggested: SuggestedPeriod[]) => {
    setNameAndSlug(place.name)
    setPlaceId(place.placeId)
    if (place.address) setAddress(place.address)
    if (place.phone) setPhone(place.phone)
    if (place.website) setWebsite(place.website)
    setGoogleHours(suggested.length ? suggested : null)
    setUseGoogleHours(suggested.length > 0)
    // Google reports the venue's current UTC offset; if the chosen zone disagrees, pick one that matches.
    if (place.utcOffsetMinutes !== null && zoneOffsetMinutes(timezone) !== place.utcOffsetMinutes) {
      const match = TIMEZONES.find((tz) => zoneOffsetMinutes(tz) === place.utcOffsetMinutes)
      if (match) setTimezone(match)
    }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const origins: string[] = []
      if (website.trim()) {
        try {
          origins.push(new URL(website.trim().startsWith('http') ? website.trim() : `https://${website.trim()}`).origin)
        } catch {
          throw new Error('That website address does not look right.')
        }
      }
      const created = await post<{ id: string }>('/v1/admin/businesses', {
        slug,
        name: name.trim(),
        timezone,
        currency,
        email: email || undefined,
        phone: phone || undefined,
        address: address || undefined,
        placeId: placeId || undefined,
        origins,
        areas: areas.filter((a) => a.name.trim()).map((a, i) => ({ ...a, sortOrder: i })),
        periods: (useGoogleHours && googleHours ? googleHours : periods).filter((p) => p.daysOfWeek.length > 0).map((p) => ({ ...p, effectiveFrom: '2020-01-01' })),
      })
      rememberBusiness(created.id)
      await refresh()
      navigate(`/b/${created.id}/settings/website`, { replace: true })
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <form className="auth-card stack-lg" onSubmit={submit} style={{ maxWidth: 560 }}>
        <div>
          <Link to="/" className="brand">
            <span className="brand-mark" /> Tabula
          </Link>
          <h1>Add your restaurant</h1>
          <p className="muted small">Step {step + 1} of 3 · {['The basics', 'Rooms and tables', 'Opening hours'][step]}</p>
        </div>
        {error ? <Alert>{error}</Alert> : null}

        {step === 0 ? (
          <div className="stack">
            {me?.capabilities?.places ? (
              <>
                <PlaceFinder onPick={applyPlace} region={currency === 'TRY' ? 'TR' : currency === 'GBP' ? 'GB' : undefined} />
                {placeId ? <p className="small" style={{ color: 'var(--ok)' }}>Details filled from Google. Check them and change anything that is wrong.</p> : <div className="divider">or type the details</div>}
              </>
            ) : null}
            <Field label="Restaurant name">{(id) => <Input id={id} value={name} onChange={(e) => setNameAndSlug(e.target.value)} required autoFocus />}</Field>
            <Field label="Web address for your booking page" hint={`Guests without your website can book at ${BOOKING_PAGE_URL}/${slug || 'your-name'}`}>
              {(id) => (
                <Input
                  id={id}
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true)
                    setSlug(slugify(e.target.value))
                  }}
                  pattern="[a-z0-9][a-z0-9-]{1,60}[a-z0-9]"
                  required
                />
              )}
            </Field>
            <div className="grid-2">
              <Field label="Time zone">
                {(id) => (
                  <Select id={id} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                    {[...new Set([timezone, ...TIMEZONES])].map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace('_', ' ')}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Currency">
                {(id) => (
                  <Select id={id} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    {['GBP', 'EUR', 'TRY', 'USD', 'AUD', 'AED'].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            <Field label="Your website" hint="Only pages on this site will be able to show the booking button. You can add more later.">
              {(id) => <Input id={id} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.example.com" inputMode="url" />}
            </Field>
            <Field label="Booking alerts email" hint="New bookings and cancellations are sent here.">
              {(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
            </Field>
            <div className="grid-2">
              <Field label="Phone (shown to guests)">{(id) => <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />}</Field>
              <Field label="Address">{(id) => <Input id={id} value={address} onChange={(e) => setAddress(e.target.value)} />}</Field>
            </div>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="stack">
            <p className="small muted">Each room or area has a number of covers (seats) that can be booked at once. Tables come later; covers are enough to start.</p>
            {areas.map((a, i) => (
              <div className="row-flex" key={i}>
                <Input value={a.name} onChange={(e) => setAreas(areas.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Area name" aria-label="Area name" style={{ flex: 2 }} />
                <Input type="number" min={1} max={2000} value={a.capacityCovers} onChange={(e) => setAreas(areas.map((x, j) => (j === i ? { ...x, capacityCovers: Number(e.target.value) } : x)))} aria-label="Covers" style={{ flex: 1 }} />
                <Button variant="ghost" size="sm" onClick={() => setAreas(areas.filter((_, j) => j !== i))} disabled={areas.length === 1} aria-label="Remove area">
                  ×
                </Button>
              </div>
            ))}
            <Button variant="secondary" size="sm" onClick={() => setAreas([...areas, { name: '', capacityCovers: 12 }])}>
              + Add an area
            </Button>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="stack-lg">
            {googleHours ? (
              <div className="card" style={{ padding: 12 }}>
                <label className="row-flex" style={{ cursor: 'pointer', alignItems: 'flex-start' }}>
                  <input type="checkbox" checked={useGoogleHours} onChange={(e) => setUseGoogleHours(e.target.checked)} style={{ marginTop: 4 }} />
                  <span className="grow">
                    <span style={{ fontWeight: 600 }}>Use the opening hours Google lists</span>
                    <PeriodsPreview periods={googleHours} />
                  </span>
                </label>
                <p className="hint">Booking times stop at the kitchen's closing time; you can trim them in Settings later.</p>
              </div>
            ) : null}
            {(useGoogleHours && googleHours ? [] : periods).map((p, i) => (
              <div className="stack" key={i}>
                <div className="row-flex">
                  <Input value={p.name} onChange={(e) => setPeriods(periods.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} aria-label="Service name" style={{ flex: 1 }} />
                  <Input type="time" value={p.starts} onChange={(e) => setPeriods(periods.map((x, j) => (j === i ? { ...x, starts: e.target.value } : x)))} aria-label="Opens" step={900} />
                  <span className="muted">to</span>
                  <Input type="time" value={p.ends} onChange={(e) => setPeriods(periods.map((x, j) => (j === i ? { ...x, ends: e.target.value } : x)))} aria-label="Kitchen closes" step={900} />
                  <Button variant="ghost" size="sm" onClick={() => setPeriods(periods.filter((_, j) => j !== i))} disabled={periods.length === 1} aria-label="Remove service">
                    ×
                  </Button>
                </div>
                <div className="checks">
                  {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                    <label key={d}>
                      <input type="checkbox" checked={p.daysOfWeek.includes(d)} onChange={(e) => setPeriods(periods.map((x, j) => (j === i ? { ...x, daysOfWeek: e.target.checked ? [...x.daysOfWeek, d] : x.daysOfWeek.filter((y) => y !== d) } : x)))} />
                      {WEEKDAYS[d]}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {!(useGoogleHours && googleHours) ? (
              <Button variant="secondary" size="sm" onClick={() => setPeriods([...periods, { name: 'Brunch', daysOfWeek: [6, 0], starts: '10:00', ends: '12:00' }])}>
                + Add a service
              </Button>
            ) : null}
            <p className="small muted">Opening to kitchen-closing times. Bookings every 15 minutes; a table for two is kept for 90 minutes, larger parties longer, and the last times offered finish by closing. Fine-tune this in Settings.</p>
          </div>
        ) : null}

        <div className="spread">
          <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={step === 0}>
            Back
          </Button>
          {/* Distinct keys: otherwise React keeps one <button> and flips its type to "submit" during the
              click that moves to the last step, and the browser's activation behaviour submits the form. */}
          {step < 2 ? (
            <Button key="next" variant="primary" onClick={() => setStep(step + 1)} disabled={step === 0 && (!name.trim() || !slug)}>
              Continue
            </Button>
          ) : (
            <Button key="create" type="submit" variant="primary" busy={busy}>
              Create restaurant
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}
